import { pb } from '../helpers/pocketbase_helper';
import MetricasCalculadora from '../utils/MetricasCalculadora';
import LogsProcessor from '../utils/LogsProcessor';

/**
 * Servicio de API para estadísticas de órdenes de producción
 * Encapsula las llamadas a PocketBase y proporciona endpoints específicos para estadísticas
 */
class EstadisticasApiService {
  /**
   * Obtiene estadísticas completas de una orden
   * @param {string} ordenId - ID de la orden
   * @returns {Promise<Object>} Estadísticas completas de la orden
   */
  static async getEstadisticasOrden(ordenId) {
    if (!ordenId) {
      throw new Error('Orden ID is required');
    }

    try {
      // Obtener datos de la orden con expansión de log_estado
      const ordenData = await pb.collection('orden_produccion').getOne(ordenId, {
        expand: 'producto,cliente,activa,finalizada'
      });


      // Obtener todos los logs de plan para esta orden directamente
      const logsPlanResult = await pb.collection('log_plan_centro_costo').getList(1, 100, {
        filter: `orden="${ordenId}"`,
        expand: 'centro_costo.operario,centro_costo.proceso_proveedor',
        sort: 'orden_plan'
      });

      const logsPlan = logsPlanResult.items || [];

      // Procesar los datos
      const procesosProcesados = LogsProcessor.extraerInformacionProcesos(logsPlan);

      // Calcular métricas
      const resumen = MetricasCalculadora.calcularEstadisticasResumen(procesosProcesados, ordenData);

      return {
        ordenData,
        procesos: procesosProcesados,
        resumen,
        metadata: {
          fetchedAt: new Date().toISOString(),
          ordenId,
          totalProcesos: procesosProcesados.length
        }
      };
    } catch (error) {
      throw new Error(`Error fetching order statistics: ${error.message}`);
    }
  }

  /**
   * Obtiene estadísticas de procesos para una orden
   * @param {string} ordenId - ID de la orden
   * @returns {Promise<Array>} Array de procesos con estadísticas
   */
  static async getProcesosEstadisticas(ordenId) {
    if (!ordenId) {
      throw new Error('Orden ID is required');
    }

    try {
      const result = await pb.collection('log_plan_centro_costo').getList(1, 100, {
        filter: `orden="${ordenId}"`,
        expand: 'centro_costo.operario,centro_costo.proceso_proveedor',
        sort: 'orden_plan'
      });

      return LogsProcessor.extraerInformacionProcesos(result.items || []);
    } catch (error) {
      throw new Error(`Error fetching process statistics: ${error.message}`);
    }
  }

  /**
   * Obtiene estadísticas de logs de estado para una orden
   * @param {string} ordenId - ID de la orden
   * @returns {Promise<Array>} Array de logs de estado
   */
  static async getLogsEstadoEstadisticas(ordenId) {
    if (!ordenId) {
      throw new Error('Orden ID is required');
    }

    try {
      const result = await pb.collection('log_estado').getList(1, 100, {
        filter: `orden_produccion="${ordenId}"`,
        expand: 'usuario,proveedor',
        sort: 'fecha_estado'
      });

      return LogsProcessor.extraerInformacionLogsEstado(result.items || []);
    } catch (error) {
      throw new Error(`Error fetching estado logs statistics: ${error.message}`);
    }
  }

  /**
   * Obtiene resumen de estadísticas para una orden
   * @param {string} ordenId - ID de la orden
   * @returns {Promise<Object>} Resumen de estadísticas
   */
  static async getResumenEstadisticas(ordenId) {
    if (!ordenId) {
      throw new Error('Orden ID is required');
    }

    try {
      // Obtener datos de la orden
      const ordenData = await pb.collection('orden_produccion').getOne(ordenId);

      // Obtener procesos
      const procesosResult = await pb.collection('log_plan_centro_costo').getList(1, 100, {
        filter: `orden="${ordenId}"`,
        expand: 'centro_costo.operario,centro_costo.proceso_proveedor',
        sort: 'orden_plan'
      });

      const procesosProcesados = LogsProcessor.extraerInformacionProcesos(procesosResult.items || []);
      const resumen = MetricasCalculadora.calcularEstadisticasResumen(procesosProcesados, ordenData);

      return resumen;
    } catch (error) {
      throw new Error(`Error fetching summary statistics: ${error.message}`);
    }
  }

  /**
   * Obtiene estadísticas de órdenes por rango de fechas
   * @param {string} fechaInicio - Fecha de inicio (ISO string)
   * @param {string} fechaFin - Fecha de fin (ISO string)
   * @returns {Promise<Array>} Array de órdenes en el rango de fechas
   */
  static async getEstadisticasPorRangoFechas(fechaInicio, fechaFin) {
    if (!fechaInicio || !fechaFin) {
      throw new Error('Start and end dates are required');
    }

    try {
      const result = await pb.collection('orden_produccion').getList(1, 100, {
        filter: `fecha_creacion >= "${fechaInicio}" && fecha_creacion <= "${fechaFin}"`,
        expand: 'producto,cliente',
        sort: '-fecha_creacion'
      });

      return result.items || [];
    } catch (error) {
      throw new Error(`Error fetching statistics by date range: ${error.message}`);
    }
  }

  /**
   * Obtiene estadísticas de un centro de costo específico
   * @param {string} centroCostoId - ID del centro de costo
   * @returns {Promise<Array>} Array de logs del centro de costo
   */
  static async getEstadisticasCentroCosto(centroCostoId) {
    if (!centroCostoId) {
      throw new Error('Centro costo ID is required');
    }

    try {
      const result = await pb.collection('log_plan_centro_costo').getList(1, 100, {
        filter: `centro_costo="${centroCostoId}"`,
        expand: 'orden,centro_costo.operario,centro_costo.proceso_proveedor',
        sort: '-inicio'
      });

      return LogsProcessor.extraerInformacionProcesos(result.items || []);
    } catch (error) {
      throw new Error(`Error fetching cost center statistics: ${error.message}`);
    }
  }

  /**
   * Obtiene estadísticas de rendimiento por operario
   * @param {string} operarioId - ID del operario
   * @returns {Promise<Object>} Estadísticas de rendimiento del operario
   */
  static async getEstadisticasOperario(operarioId) {
    if (!operarioId) {
      throw new Error('Operario ID is required');
    }

    try {
      // Obtener logs donde el operario participó
      const result = await pb.collection('log_plan_centro_costo').getList(1, 100, {
        filter: `centro_costo.operario="${operarioId}"`,
        expand: 'orden,centro_costo',
        sort: '-inicio'
      });

      const logs = result.items || [];
      const procesos = LogsProcessor.extraerInformacionProcesos(logs);

      // Calcular estadísticas del operario
      const tiempoTotal = MetricasCalculadora.calcularTiempoTotal(procesos);
      const procesosCompletados = procesos.filter(p => p.estado === 'Completado').length;
      const eficienciaPromedio = MetricasCalculadora.calcularEficienciaPromedio(procesos);

      return {
        operarioId,
        totalProcesos: procesos.length,
        procesosCompletados,
        tiempoTotal,
        eficienciaPromedio,
        procesos,
        metadata: {
          fetchedAt: new Date().toISOString(),
          totalLogs: logs.length
        }
      };
    } catch (error) {
      throw new Error(`Error fetching operario statistics: ${error.message}`);
    }
  }

  /**
   * Obtiene estadísticas generales del sistema
   * @returns {Promise<Object>} Estadísticas generales
   */
  static async getEstadisticasGenerales() {
    try {
      // Obtener estadísticas de órdenes
      const ordenesResult = await pb.collection('orden_produccion').getList(1, 1000, {
        filter: 'activa = true',
        sort: '-fecha_creacion'
      });

      // Obtener estadísticas de procesos
      const procesosResult = await pb.collection('log_plan_centro_costo').getList(1, 1000, {
        sort: '-inicio'
      });

      // Obtener estadísticas de centros de costo
      const centrosResult = await pb.collection('centro_costo').getList(1, 100, {
        filter: 'activo = true'
      });

      const ordenes = ordenesResult.items || [];
      const procesos = LogsProcessor.extraerInformacionProcesos(procesosResult.items || []);
      const centros = centrosResult.items || [];

      return {
        totalOrdenes: ordenes.length,
        totalProcesos: procesos.length,
        totalCentrosCosto: centros.length,
        ordenesActivas: ordenes.filter(o => o.activa && !o.finalizada).length,
        ordenesFinalizadas: ordenes.filter(o => o.finalizada).length,
        procesosCompletados: procesos.filter(p => p.estado === 'Completado').length,
        metadata: {
          fetchedAt: new Date().toISOString(),
          lastUpdate: new Date().toISOString()
        }
      };
    } catch (error) {
      throw new Error(`Error fetching general statistics: ${error.message}`);
    }
  }

  /**
   * Valida si una orden existe y está disponible para estadísticas
   * @param {string} ordenId - ID de la orden
   * @returns {Promise<boolean>} True si la orden existe y está disponible
   */
  static async validarOrdenDisponible(ordenId) {
    if (!ordenId) return false;

    try {
      const orden = await pb.collection('orden_produccion').getOne(ordenId);
      return orden && orden.id;
    } catch (error) {
      return false;
    }
  }

  /**
   * Obtiene metadatos de una orden sin cargar todos los datos
   * @param {string} ordenId - ID de la orden
   * @returns {Promise<Object>} Metadatos de la orden
   */
  static async getMetadatosOrden(ordenId) {
    if (!ordenId) {
      throw new Error('Orden ID is required');
    }

    try {
      const orden = await pb.collection('orden_produccion').getOne(ordenId, {
        fields: 'id,numero,fecha_creacion,fecha_inicio,fecha_finalizacion,activa,finalizada'
      });

      return {
        id: orden.id,
        numero: orden.numero,
        fechaCreacion: orden.fecha_creacion,
        fechaInicio: orden.fecha_inicio,
        fechaFinalizacion: orden.fecha_finalizacion,
        activa: orden.activa,
        finalizada: orden.finalizada,
        disponible: true
      };
    } catch (error) {
      return {
        id: ordenId,
        disponible: false,
        error: error.message
      };
    }
  }
}

export default EstadisticasApiService;
